import React from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';

// components
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle';
import { SbRespRefineMenu } from '../SbRespRefineMenu/SbRespRefineMenu';
import { SbSharedPagination } from '../SbSharedPagination/SbSharedPagination';
import { LoadingComponent } from '../../components/global/LoadingComponent';
import { SbRespContentModuleSelector } from '../SbRespContentModuleSelector/SbRespContentModuleSelector';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs';
import { SbRespSearchResultContainer } from '../SbRespSearchResultContainer/SbRespSearchResultContainer';
import { SbRespBrowseHeader } from '../SbRespBrowseHeader/SbRespBrowseHeader';
import { SbRespSortItems } from '../SbRespSortItems/SbRespSortItems';
import { SbRespPageSize } from '../SbRespPageSize/SbRespPageSize';
import { SbSharedBillboardAd } from '../SbSharedBillboardAd/SbSharedBillboardAd';
import { SbSharedSkyscraperAd } from '../SbSharedSkyscraperAd/SbSharedSkyscraperAd';
import { SbRespNullSearchResults } from '../SbRespNullSearchResults/SbRespNullSearchResults';
import { SbSharedRelatedItems } from '../SbSharedRelatedItems/SbSharedRelatedItems';
import { SbRespMoreWaysToBrowse } from '../SbRespMoreWaysToBrowse/SbRespMoreWaysToBrowse';
import { SbRespNotificationUnavailableItem } from '../SbRespNotifications/SbRespNotificationUnavailableItem/SbRespNotificationUnavailableItem';
import { SbRespStickyMenu } from '../SbRespStickyMenu/SbRespStickyMenu';
import { SharedRecentlyViewedItems } from '../../shared/SharedRecentlyViewedItems/SharedRecentlyViewedItems';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';
import { TradePromoLoader } from '../../global/TradePromo/TradePromoLoader';
import { SbSharedPersonalizedFilterToast } from '../SbSharedPersonalizedFilterToast/SbSharedPersonalizedFilterToast';

// constants
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
import { FILTER_REFINE_SEARCH } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants';
import { MIN_THIN_PAGE_COUNT } from '../../constants/sbConstants';
import { VERTICALS } from '../../constants/uriConstants';
const { SEARCH } = pageTypes;

// styles
import styles from '../SbRespComponents/SbRespComponents.scss';

function getSearchTerm(filters, isSearchPage) {
    const searchKey = isSearchPage ? SEARCH : FILTER_REFINE_SEARCH;

    return get(filters.filter(({ name }) => name === searchKey), '[0].values[0].urlLabel');
}

function SbRespCollectionsComponentsComponent(props) {
    const {
        viewer,
        currency,
        searchBrowse,
        user,
        showMeasurements,
        page,
        generatedUriRef,
        refetchInFlight,
        isClient,
        relay,
        relayVariables,
        shippingFilters,
        collectionsModules,
    } = props;

    const {
        displayUriRef,
        filters,
        topCategoryL1,
        displayMaxNumberOfPages,
        totalResults,
        appliedFilters,
        pageType,
    } = searchBrowse;

    const {
        isContentModuleEnabled,
        isBillboardAdEnabled,
        isMoreWaysToBrowseEnabled,
        isSkyscraperAdEnabled,
        isThinPageEnabled,
    } = viewer;
    const { canShowInternalSellerName, canShowSellerName } = user || {};

    const isSearch = pageType === SEARCH;
    const isFurniture = topCategoryL1 === VERTICALS.FURNITURE;
    const canShowCreatorName = !canShowSellerName && isFurniture;
    const showNullSearchResultPage = !totalResults;
    const isThinPage = totalResults < MIN_THIN_PAGE_COUNT && isThinPageEnabled;
    const searchTerm = getSearchTerm(appliedFilters, isSearch);
    const gridType = get(collectionsModules, 'gridType') || null;

    const sharedProps = {
        currency,
        searchBrowse,
        viewer,
        user,
        isClient,
    };

    if (showNullSearchResultPage) {
        return (
            <SbRespNullSearchResults
                user={user}
                page={page}
                viewer={viewer}
                searchTerm={searchTerm}
                uriRef={generatedUriRef}
                searchBrowse={searchBrowse}
            />
        );
    }

    return (
        <React.Fragment>
            {/* Breadcrumbs */}
            <SbSharedPageBreadcrumbs searchBrowse={searchBrowse} />

            {/* Page Header */}
            <div className={styles.headerContainer}>
                <SbSharedTitle searchBrowse={searchBrowse} searchTerm={searchTerm} />
                {relayVariables.isTrade && isClient && (
                    <div className={styles.tradePromoMessage}>
                        <TradePromoLoader showIcon user={user} viewer={viewer} />
                    </div>
                )}
            </div>

            <div className={styles.mainContentContainer}>
                <ServerVarsContextConsumer>
                    {({ isTablet }) =>
                        !isTablet ? (
                            <SbRespStickyMenu>
                                {/* Refine Menu */}
                                <SbRespRefineMenu
                                    {...sharedProps}
                                    filters={filters}
                                    uriRef={generatedUriRef}
                                    isTrade={relayVariables.isTrade}
                                />

                                {/* Ad */}
                                {!isThinPage && isSkyscraperAdEnabled && (
                                    <SbSharedSkyscraperAd searchBrowse={searchBrowse} />
                                )}
                            </SbRespStickyMenu>
                        ) : (
                            <div className={styles.refineMenuContainer}>
                                {/* Refine Menu */}
                                <SbRespRefineMenu
                                    {...sharedProps}
                                    filters={filters}
                                    uriRef={generatedUriRef}
                                />

                                {/* Ad */}
                                {!isThinPage && isSkyscraperAdEnabled && (
                                    <SbSharedSkyscraperAd searchBrowse={searchBrowse} />
                                )}
                            </div>
                        )
                    }
                </ServerVarsContextConsumer>

                <div className={styles.resultsContainer}>
                    {/* Content Module */}
                    {isContentModuleEnabled && (
                        <SbRespContentModuleSelector searchBrowse={searchBrowse} />
                    )}
                    <div className={styles.resultsHeader}>
                        <div className={styles.sortContainer}>
                            <SbRespSortItems searchBrowse={searchBrowse} />
                            <SbRespPageSize searchBrowse={searchBrowse} />
                        </div>
                        <SbRespBrowseHeader searchBrowse={searchBrowse} />
                    </div>

                    {/* Item Results */}
                    <SbRespSearchResultContainer
                        {...sharedProps}
                        showInternalSellerName={canShowInternalSellerName}
                        showSellerName={!!canShowSellerName}
                        showCreatorName={canShowCreatorName}
                        showMeasurements={showMeasurements}
                        relay={relay}
                        gridType={gridType}
                    />
                    {/* Related Items */}
                    {isThinPage && (
                        <SbSharedRelatedItems
                            searchBrowse={searchBrowse}
                            user={user}
                            currency={currency}
                        />
                    )}
                    {/* Pagination */}
                    <div className={styles.paginationContainer}>
                        <SbSharedPagination
                            currentPage={page}
                            numberOfPages={displayMaxNumberOfPages}
                            displayUriRef={displayUriRef}
                        />
                    </div>
                </div>
            </div>

            {/* Ad */}
            {!isThinPage && isBillboardAdEnabled && (
                <SbSharedBillboardAd searchBrowse={searchBrowse} adWidth="desktop" />
            )}

            {/* Recently Viewed Items */}
            {<SharedRecentlyViewedItems isClient={isClient} user={user} />}

            {/*  More Ways to Browse */}
            {isMoreWaysToBrowseEnabled && <SbRespMoreWaysToBrowse searchBrowse={searchBrowse} />}

            {/* Overlay Loader */}
            {refetchInFlight && <LoadingComponent />}

            {/* Unavailable Item Modal */}
            <SbRespNotificationUnavailableItem />

            {/* Personalized Filter Toast */}
            <SbSharedPersonalizedFilterToast shippingFilters={shippingFilters} />
        </React.Fragment>
    );
}

SbRespCollectionsComponentsComponent.propTypes = {
    searchBrowse: PropTypes.shape({
        displayUriRef: PropTypes.string.isRequired,
        topCategoryL1: PropTypes.string,
        filters: PropTypes.array.isRequired,
    }).isRequired,
    page: PropTypes.number,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    viewer: PropTypes.object,
    refetchInFlight: PropTypes.bool,
    showMeasurements: PropTypes.bool,
    generatedUriRef: PropTypes.string,
    relay: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    internalBuyLinks: PropTypes.object,
    relayVariables: PropTypes.shape({
        isTrade: PropTypes.bool,
    }).isRequired,
    shippingFilters: PropTypes.array,
    collectionsModules: PropTypes.object,
};

const mapStateToProps = state => {
    const { displayPreferences, filters, relayVariables } = state;

    return {
        showMeasurements: displayPreferences.showMeasurements,
        generatedUriRef: filters.generatedUriRef,
        page: filters.page,
        refetchInFlight: filters.refetchInFlight,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespCollectionsComponents = createFragmentContainer(
    connect(mapStateToProps)(SbRespCollectionsComponentsComponent),
    {
        searchBrowse: graphql`
            fragment SbRespCollectionsComponents_searchBrowse on SearchBrowseQueryConnection {
                displayUriRef
                topCategoryL1
                totalResults
                displayMaxNumberOfPages
                pageType
                ...SbRespRefineMenu_searchBrowse
                ...SbRespContentModuleSelector_searchBrowse
                ...SbRespNullSearchResults_searchBrowse
                ...SbSharedPageBreadcrumbs_searchBrowse
                ...SbRespSearchResultContainer_searchBrowse
                ...SbSharedTitle_searchBrowse
                ...SbRespBrowseHeader_searchBrowse
                ...SbRespSortItems_searchBrowse
                ...SbRespPageSize_searchBrowse
                ...SbSharedBillboardAd_searchBrowse
                ...SbSharedSkyscraperAd_searchBrowse
                ...SbSharedRelatedItems_searchBrowse
                ...SbRespMoreWaysToBrowse_searchBrowse
                ...SbRespTradeOnApprovalPromoBanner_searchBrowse @include(if: $isTrade)
                filters {
                    ...SbRespRefineMenu_filters
                }
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
            }
        `,
        user: graphql`
            fragment SbRespCollectionsComponents_user on User {
                canShowSellerName
                canShowInternalSellerName: hasPermission(name: "VIEW_SELLER_NAME")
                ...TradePromo_user @include(if: $isTrade)
                ...SbRespRefineMenu_user
                ...SbRespNullSearchResults_user
                ...SbRespSearchResultContainer_user
                ...SbSharedRelatedItems_user
                ...SharedRecentlyViewedItems_user
            }
        `,
        viewer: graphql`
            fragment SbRespCollectionsComponents_viewer on Viewer {
                isSkyscraperAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isContentModuleEnabled: isEnabledByLocale(featureName: "BROWSE_CONTENT_MODULES")
                isBillboardAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isThinPageEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isMoreWaysToBrowseEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                ...SbRespSearchResultContainer_viewer
                ...SbRespRefineMenu_viewer
                ...SbRespNullSearchResults_viewer
                ...TradePromo_viewer @include(if: $isTrade)
            }
        `,
        shippingFilters: graphql`
            fragment SbRespCollectionsComponents_shippingFilters on ShippingFilterType
                @relay(plural: true) {
                ...SbSharedPersonalizedFilterToast_shippingFilters
            }
        `,
        collectionsModules: graphql`
            fragment SbRespCollectionsComponents_collectionsModules on CollectionsModulesMainContent {
                gridType
            }
        `,
    }
);
